import { z } from 'zod';
import type { NotificationCategory } from './enums.js';

/**
 * Notification preference groups. People choose channels per group (not per individual alert type);
 * in-app notifications are always on.
 */
export const NOTIFICATION_GROUPS = ['tasks', 'announcements', 'payments', 'shipments', 'tickets', 'general'] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export const NOTIFICATION_GROUP_LABELS: Record<NotificationGroup, string> = {
  tasks: 'Tasks (new, reassigned, due soon, overdue)',
  announcements: 'Announcements',
  payments: 'Payments & payouts',
  shipments: 'Shipments, mail & labels',
  tickets: 'Tickets & messages',
  general: 'Everything else',
};

/** Channels a person can switch on or off (in-app is always on). */
export const PREFERENCE_CHANNELS = ['whatsapp', 'sms', 'email', 'push'] as const;
export type PreferenceChannel = (typeof PREFERENCE_CHANNELS)[number];

export const PREFERENCE_CHANNEL_LABELS: Record<PreferenceChannel, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  email: 'Email',
  push: 'Phone alerts (push)',
};

const GROUP_OF: Partial<Record<NotificationCategory, NotificationGroup>> = {
  task_new: 'tasks',
  task_overdue: 'tasks',
  task_reassigned: 'tasks',
  followup_due: 'tasks',
  followup_escalated: 'tasks',
  announcement: 'announcements',
  payment_review_required: 'payments',
  payment_authorized: 'payments',
  payment_paid: 'payments',
  rep_settlement: 'payments',
  rep_expense: 'payments',
  opspay: 'payments',
  opspay_sla: 'payments',
  invoice: 'payments',
  payout: 'payments',
  payout_sla: 'payments',
  credit_low: 'payments',
  shipment_created: 'shipments',
  label_purchased: 'shipments',
  tracking_updated: 'shipments',
  shipment_in_transit: 'shipments',
  shipment_out_for_delivery: 'shipments',
  shipment_delivered: 'shipments',
  delivery_exception: 'shipments',
  rep_mail: 'shipments',
  rep_label: 'shipments',
  rep_dropoff: 'shipments',
  ticket_created: 'tickets',
  ticket_escalated: 'tickets',
  ticket_assigned: 'tickets',
  ticket_resolved: 'tickets',
  ticket_reopened: 'tickets',
  sla_warning: 'tickets',
  whatsapp: 'tickets',
  client_inquiry: 'tickets',
  rep_message: 'tickets',
};

export function notificationGroup(category: NotificationCategory): NotificationGroup {
  return GROUP_OF[category] ?? 'general';
}

/**
 * Defaults when the person has not chosen yet (staff and representatives). WhatsApp is on for every group — it is
 * only used when the person has a WhatsApp number on file, and very noisy alert types stay off WhatsApp/SMS (see
 * WHATSAPP_QUIET_CATEGORIES). Urgent alerts always go out by email and phone regardless (see notify()).
 */
const DEFAULTS: Record<NotificationGroup, Record<PreferenceChannel, boolean>> = {
  tasks: { whatsapp: true, sms: false, email: false, push: true },
  announcements: { whatsapp: true, sms: false, email: false, push: true },
  payments: { whatsapp: true, sms: false, email: false, push: true },
  shipments: { whatsapp: true, sms: false, email: false, push: true },
  tickets: { whatsapp: true, sms: false, email: false, push: true },
  general: { whatsapp: true, sms: false, email: false, push: true },
};

/** Who the defaults are for: client-portal accounts never get WhatsApp by default. */
export type PreferenceAudience = 'staff' | 'client';

/**
 * Very frequent alert types that never go to WhatsApp / SMS by default, even when WhatsApp is on for their group
 * (in-app, email and push only). A person can still opt in for the alert type itself.
 */
export const WHATSAPP_QUIET_CATEGORIES: NotificationCategory[] = ['tracking_updated', 'shipment_in_transit'];
export const WHATSAPP_QUIET_NOTE = 'Courier tracking updates and “in transit” scans are not sent to WhatsApp or SMS (too frequent) — they stay in-app, by email and phone alerts.';

/**
 * Key alerts that are emailed while the person has not chosen email for their group yet (the email column shows
 * off until then; switching email on or off for the group replaces this default).
 */
export const EMAIL_BY_DEFAULT: NotificationCategory[] = [
  'delivery_exception',
  'ticket_escalated',
  'ticket_assigned',
  'task_overdue',
  'payment_review_required',
  'payment_authorized',
  'contract_expiring',
  'lease_expiring',
  'daily_report_ready',
  'sla_warning',
];

export function defaultGroupChannel(group: NotificationGroup, channel: PreferenceChannel, category?: NotificationCategory, audience: PreferenceAudience = 'staff'): boolean {
  if (channel === 'email' && category && EMAIL_BY_DEFAULT.includes(category)) return true;
  if (channel === 'whatsapp' && audience === 'client') return false;
  return DEFAULTS[group][channel];
}

/** Phone channels (WhatsApp / SMS) skip the quiet alert types unless the person opted in for that type. */
export const isQuietOnPhone = (channel: PreferenceChannel, category?: NotificationCategory) => (channel === 'whatsapp' || channel === 'sms') && !!category && WHATSAPP_QUIET_CATEGORIES.includes(category);

export const notificationGroupPreferenceInput = z.object({
  preferences: z
    .array(
      z.object({
        group: z.enum(NOTIFICATION_GROUPS),
        channel: z.enum(PREFERENCE_CHANNELS),
        enabled: z.boolean(),
      }),
    )
    .max(NOTIFICATION_GROUPS.length * PREFERENCE_CHANNELS.length),
});
export type NotificationGroupPreferenceInput = z.infer<typeof notificationGroupPreferenceInput>;

/** "Also send via WhatsApp" choice made by the person who assigns / notifies (absent = recipient's preference). */
export const notifyChoiceInput = z.object({ notifyWhatsApp: z.boolean().optional() });

/** Per-recipient result of a WhatsApp / SMS broadcast (announcements). */
export const BROADCAST_STATUSES = ['pending', 'sent', 'failed', 'no_number', 'opted_out', 'duplicate', 'skipped'] as const;
export type BroadcastStatus = (typeof BROADCAST_STATUSES)[number];
export const BROADCAST_STATUS_LABELS: Record<BroadcastStatus, string> = {
  pending: 'Queued',
  sent: 'Sent',
  failed: 'Failed',
  no_number: 'No number',
  opted_out: 'Opted out',
  duplicate: 'Same number as another recipient',
  skipped: 'Not sent',
};

/** WhatsApp inbound keywords that opt a number out of / back into broadcasts. */
export const WHATSAPP_OPT_OUT_WORDS = ['STOP', 'UNSUBSCRIBE', 'STOPALL', 'CANCEL', 'END', 'QUIT'] as const;
export const WHATSAPP_OPT_IN_WORDS = ['START', 'SUBSCRIBE', 'UNSTOP'] as const;

/**
 * Approved WhatsApp template texts (Twilio Content Template Builder, category UTILITY). The free-form text sent
 * inside the 24-hour window uses the same wording, so the message reads the same either way.
 */
export const WHATSAPP_TEMPLATES = {
  task: { configField: 'templateTaskSid', text: 'New task: {{1}} — due {{2}}. Open: {{3}}' },
  announcement: { configField: 'templateAnnouncementSid', text: 'Foreign Founders announcement: {{1}} — {{2}}' },
  alert: { configField: 'templateAlertSid', text: '{{1}}: {{2}}. Open: {{3}}' },
} as const;
export type WhatsAppTemplateKind = keyof typeof WHATSAPP_TEMPLATES;

export function renderTemplate(kind: WhatsAppTemplateKind, vars: Record<string, string>): string {
  return WHATSAPP_TEMPLATES[kind].text.replace(/\{\{(\d)\}\}/g, (_, n: string) => vars[n] ?? '');
}

// ---------------------------------------------------------------------------
// Delivery tracking (Twilio status callbacks) and the admin Delivery log
// ---------------------------------------------------------------------------

/** Twilio message statuses reported by the status callback, in the order they normally happen. */
export const PROVIDER_STATUSES = ['accepted', 'scheduled', 'queued', 'sending', 'sent', 'delivered', 'read', 'failed', 'undelivered'] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];
export const isProviderFailure = (s: string | null | undefined) => s === 'failed' || s === 'undelivered';

/** What the Delivery log shows per row (computed from the stored status, provider status and note). */
export const DELIVERY_LOG_STATUSES = ['pending', 'deferred', 'sent', 'delivered', 'read', 'failed', 'undelivered', 'opted_out', 'no_number', 'skipped'] as const;
export type DeliveryLogStatus = (typeof DELIVERY_LOG_STATUSES)[number];
export const DELIVERY_LOG_STATUS_LABELS: Record<DeliveryLogStatus, string> = {
  pending: 'Pending',
  deferred: 'Deferred (rate limit)',
  sent: 'Sent (no receipt yet)',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
  undelivered: 'Undelivered',
  opted_out: 'Opted out',
  no_number: 'No number',
  skipped: 'Not sent',
};

export interface TwilioErrorInfo {
  code: string;
  /** Plain-language explanation of what went wrong. */
  explanation: string;
  /** What to do about it. */
  action: string;
}

/** Common Twilio WhatsApp / SMS error codes in plain language (https://www.twilio.com/docs/api/errors). */
export const TWILIO_ERRORS: Record<string, Omit<TwilioErrorInfo, 'code'>> = {
  '63016': {
    explanation: 'Outside the 24-hour WhatsApp window: free-form messages are only allowed within 24 hours of the person’s last WhatsApp message',
    action: 'Configure the approved alert / task templates (Admin → Integrations → Twilio), or ask the person to send any WhatsApp message to the business number to open the window',
  },
  '63003': {
    explanation: 'The number is not a WhatsApp number (or is not reachable on WhatsApp)',
    action: 'Check the WhatsApp number on the person’s profile, including the country code (e.g. +977 for Nepal)',
  },
  '63024': {
    explanation: 'WhatsApp (Meta) rejected the recipient or the message — e.g. the number is invalid for WhatsApp',
    action: 'Check the number and its country code; ask the person to confirm WhatsApp is installed on it',
  },
  '63049': {
    explanation: 'Meta chose not to deliver this marketing message (per-user marketing limit)',
    action: 'Use templates in the UTILITY category for alerts (not MARKETING) and re-submit them in Twilio',
  },
  '63027': {
    explanation: 'The WhatsApp template could not be sent (template unavailable, not approved, or the variables do not match)',
    action: 'Check the template SID and its approval status in Twilio Content Template Builder; the variables must be {{1}} {{2}} {{3}}',
  },
  '63112': {
    explanation: 'Meta disabled or paused the WhatsApp Business account or the template',
    action: 'Open Twilio → Messaging → Senders → WhatsApp senders and Meta Business Manager to see why the account / template is restricted',
  },
  '63013': {
    explanation: 'The message violates a WhatsApp policy',
    action: 'Review the template / message wording in Meta Business Manager',
  },
  '63018': {
    explanation: 'WhatsApp rate limit for this business number was exceeded',
    action: 'Wait and retry; ask Twilio / Meta to raise the messaging tier if it keeps happening',
  },
  '63032': {
    explanation: 'The person cannot receive business messages yet (e.g. pending WhatsApp terms update)',
    action: 'Ask the person to open WhatsApp, accept any pending updates, then send a test message',
  },
  '63051': {
    explanation: 'The WhatsApp Business account is locked',
    action: 'Check Meta Business Manager for account restrictions',
  },
  '21610': {
    explanation: 'The person replied STOP to our SMS number (opted out)',
    action: 'The person must text START to the SMS number to receive SMS again',
  },
  '21211': {
    explanation: 'The phone number is not valid',
    action: 'Correct the number on the person’s profile (E.164 with country code, e.g. +9779812345678)',
  },
  '21408': {
    explanation: 'SMS to this country is not enabled on the Twilio account',
    action: 'Enable the country in Twilio → Messaging → Settings → Geo permissions',
  },
  '21612': {
    explanation: 'Twilio cannot route a message from our number to this number',
    action: 'Check that the sender supports this destination (e.g. international SMS from a US number)',
  },
  '21614': {
    explanation: 'The number is not a mobile number',
    action: 'Use a mobile number for SMS / WhatsApp',
  },
  '30003': {
    explanation: 'The phone is unreachable (switched off or out of coverage)',
    action: 'Usually temporary — ask the person whether the phone was on and try a test message later',
  },
  '30004': {
    explanation: 'The message was blocked (the person or the carrier blocked the number)',
    action: 'Ask the person to unblock the business number',
  },
  '30005': {
    explanation: 'Unknown destination: the number does not exist or is no longer in service',
    action: 'Check the number on the person’s profile',
  },
  '30006': {
    explanation: 'Landline or unreachable carrier',
    action: 'Use a mobile number',
  },
  '30007': {
    explanation: 'The carrier filtered the message as spam',
    action: 'For US numbers register A2P 10DLC; otherwise rely on WhatsApp',
  },
  '30008': {
    explanation: 'Unknown delivery error reported by the carrier',
    action: 'Retry with a test message; contact Twilio support if it continues',
  },
  '30034': {
    explanation: 'US SMS from an unregistered 10DLC number is blocked',
    action: 'Complete A2P 10DLC registration in Twilio',
  },
};

/** Plain-language explanation for a Twilio error code (unknown codes get a generic text and the docs link). */
export function twilioErrorInfo(code: string | number | null | undefined): TwilioErrorInfo | null {
  if (code === null || code === undefined || code === '') return null;
  const c = String(code).trim();
  const known = TWILIO_ERRORS[c];
  if (known) return { code: c, ...known };
  return { code: c, explanation: `Twilio error ${c}`, action: `See https://www.twilio.com/docs/api/errors/${encodeURIComponent(c)}` };
}
